import { useRef, useEffect } from 'react';
import { DEFAULT_LANGUAGE, SITE_NAME } from '../config/constants';

export function getLanguageByPath(path, fallback = DEFAULT_LANGUAGE) {
  return /^\/[a-z]{2}(\/.*)?$/g.test(path)
    ? path.replace(/^\/([a-z]{2})(\/.*)?/, '$1')
    : fallback;
}

export function getDocumentTitle(title) {
  if (!title) {
    return SITE_NAME;
  }

  return `${title} | ${SITE_NAME}`;
}

// Helper function to map all the content types to the corresponding field
export const getContentSubType = (nodeType) => {

  switch (nodeType) {
    case 'article': return 'field_article_type';
    case 'download': return 'field_download_type';
    case 'onderzoek': return 'field_research_type';
    case 'event': return 'field_event_type';
    case 'expert': return 'field_expert_type';
    case 'praktijkvoorbeeld': return 'field_practicalexample_type';
    case 'tool': return 'field_tool_type';
    case 'e_learning': return 'field_elearning_type';
    case 'interventie': return 'field_intervention_type';
    case 'vacature': return 'field_vacancy_type';
    default: break;
  }
};

// Sorting function for strings
export function sortLabelsAscending(a, b) {
  const nameA = a.label.toUpperCase(); // ignore upper and lowercase
  const nameB = b.label.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  // names must be equal
  return 0;
}

export function sortLabelsDescending(a, b) {
  const nameA = a.label.toUpperCase(); // ignore upper and lowercase
  const nameB = b.label.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return 1;
  }
  if (nameA > nameB) {
    return -1;
  }

  // names must be equal
  return 0;
}

export default function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}

// This is a quick workaround for dealing with SVG images: we do not
// want to apply the image style to SVG images. A better fix would be
// to also index the SVG image url without an image style applied.
// @see https://www.drupal.org/project/drupal/issues/3091740
export function removeImageStyleFromSvgUrl(url) {
  if (url && url.match(/\.svg/) && url.match(/styles\/.*\/public\//)) {
    // Remove the image style part from the url.
    url = url.replace(/styles\/.*\/public\//, '');
  }

  return url;
}

export function prefixObjectKeys(prefix, obj, whitelist) {
  return obj ? Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      whitelist.includes(key) ? `${prefix}${key}` : key,
      value,
    ]),
  ) : {};
}

// Check if value is available in Spotler list, otherwise fill the custom field.
export function prepareSpotlerListData(workFieldObj, jobTitleObject) {
  const result = {};
  // Set selection in predefined Spotler options.
  result.list1 = workFieldObj.fields.map((obj) => ({
    ...obj,
    enabled: obj.description === workFieldObj.selected,
  }));
  result.list2 = jobTitleObject.fields.map((obj) => ({
    ...obj,
    enabled: obj.description === jobTitleObject.selected,
  }));

  // Get spotler records of selected value.
  const workFieldValue = workFieldObj.fields.find((e) => e.description === workFieldObj.selected);
  const jobListValue = jobTitleObject.fields.find((e) => e.description === jobTitleObject.selected);

  result.freeField2 = '';
  // Check if this is a Spotler option or not.
  if (!workFieldValue) {
    if (workFieldObj.selected_other) {
      result.freeField2 = workFieldObj.selected_other;
    } else {
      result.freeField2 = workFieldObj.selected;
    }
  }

  result.freeField1 = '';
  // Check if this is a Spotler option or not.
  if (!jobListValue) {
    if (jobTitleObject.selected_other && jobTitleObject.selected_other === jobTitleObject.selected) {
      result.freeField1 = jobTitleObject.selected_other;
    } else {
      result.freeField1 = jobTitleObject.selected;
    }
  }

  return result;
}